// @ts-check
import {
  GVL,
  PurposeRestriction,
  RestrictionType,
  TCModel,
  TCString
} from '@mailonline/tcf-core'
import { createLog } from '../utils/logger'
import { TcModel } from './tcModel'
import packageJson from '../../package.json'

const { cmp } = packageJson
const log = createLog()

/**
 * @param {{gvlRaw: GVLRaw, publisherConsents: PublisherConsents, publisherRestrictions: Array<number>}} opts
 * @returns {Promise<TcModel>}
 */
async function createTcModelFromGvl({
  gvlRaw,
  publisherConsents,
  publisherRestrictions
}) {
  log('creating TCModel for new user')
  const tcModel = new TCModel()
  tcModel.cmpId = 27
  tcModel.cmpVersion = cmp.version || 2
  // siteSpecific Storage (ie not global)
  tcModel.isServiceSpecific = true
  tcModel.publisherCountryCode = 'GB'
  const gvl = new GVL(gvlRaw.json)
  tcModel.gvl = gvl
  tcModel.publisherConsents.set(
    publisherConsents.purposes || [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
  )
  tcModel.publisherLegitimateInterests.set(
    publisherConsents.legIntPurposes || [5, 6, 8, 10]
  )

  await tcModel.gvl.readyPromise

  const vendors = Object.values(tcModel.gvl.vendors).map(
    ({ id, flexiblePurposes, legIntPurposes }) => ({
      vendorId: id,
      flexiblePurposes: new Set(flexiblePurposes),
      legIntPurposes: new Set(legIntPurposes)
    })
  )

  if (gvl.tcfPolicyVersion === 2) {
    publisherRestrictions.forEach((purposeId) => {
      const purposeRestriction = new PurposeRestriction()
      purposeRestriction.purposeId = purposeId
      purposeRestriction.restrictionType = RestrictionType.REQUIRE_CONSENT
      vendors.forEach(({ vendorId, flexiblePurposes, legIntPurposes }) => {
        try {
          if (
            flexiblePurposes.has(purposeId) &&
            legIntPurposes.has(purposeId)
          ) {
            // vendorID and restriction
            log(`restricting purposeId: ${purposeId} for vendorId: ${vendorId}`)
            tcModel.publisherRestrictions.add(vendorId, purposeRestriction)
          }
        } catch (e) {
          log('unable to restrict', purposeId, vendorId)
        }
      })
    })
  }

  const specialPurposeOnlyDeclared = Object.values(tcModel.gvl.vendors).filter(
    (v) =>
      v.purposes.length === 0 &&
      v.legIntPurposes.length === 0 &&
      v.specialPurposes.length > 0
  )
  log('specialPurposeOnly', specialPurposeOnlyDeclared)

  return new TcModel({
    gvl,
    molGvlVersion: gvlRaw.version.toString(),
    model: tcModel,
    specialPurposeOnlyDeclared
  })
}

/**
 * @param {GVLRaw} gvlRaw
 * @param {string} tcString
 * @returns {TcModel}
 */
function createTcModelFromTcString(gvlRaw, tcString) {
  const gvl = new GVL(gvlRaw.json)
  const [tcModel, specialPurposeOnlyDeclared] = createModelFromTcString(
    gvl,
    tcString
  )
  return new TcModel({
    gvl,
    molGvlVersion: gvlRaw.version.toString(),
    model: tcModel,
    specialPurposeOnlyDeclared
  })
}

/**
 * @param {{gvlRaw: GVLRaw, existingTcModel: TcModel, keepDate: boolean, publisherRestrictions: Array<number>, publisherConsents: PublisherConsents}} tcModelOpts
 * @returns {Promise<TcModel>}
 */
async function createTcModelForRepromptedUser({
  gvlRaw,
  existingTcModel,
  keepDate = false,
  publisherRestrictions,
  publisherConsents
}) {
  const tcModel = await createTcModelFromGvl({
    gvlRaw,
    publisherRestrictions,
    publisherConsents
  })
  let opts = formPreferenceOpts(existingTcModel)
  if (keepDate) {
    opts = {
      ...opts,
      created: existingTcModel.model.created,
      lastUpdated: existingTcModel.model.lastUpdated
    }
  }
  tcModel.updateUserPreferences(opts)
  return tcModel
}

/**
 * @param {TcModel} existingTcModel
 * @returns {PreferenceOpts}
 */
function formPreferenceOpts(existingTcModel) {
  return {
    consentScreen: existingTcModel.model.consentScreen,
    vendorConsents: getPreferencesOpt('vendorConsents'),
    vendorLegitimateInterests: getPreferencesOpt('vendorLegitimateInterests'),
    purposeConsents: getPreferencesOpt('purposeConsents'),
    purposeLegitimateInterests: getPreferencesOpt('purposeLegitimateInterests'),
    specialFeatureOptins: getPreferencesOpt('specialFeatureOptins')
  }

  /**
   * @param {keyof TCModel} key
   */
  function getPreferencesOpt(key) {
    return Array.from(existingTcModel.model[key].set_).map((v) => parseInt(v))
  }
}

/**
 * @param {{existingGvlRaw: GVLRaw, newGvlRaw: GVLRaw, tcString: string}} opts
 */
function createTcModelForExitingUserSimplifiedGvl({
  existingGvlRaw,
  newGvlRaw,
  tcString
}) {
  const existingTcModel = createTcModelFromTcString(existingGvlRaw, tcString)
  // need to use existing restricted purposes, and existing publisher consents
  // existingTcModel.model.publisherConsents

  const publisherConsents = {
    purposes: [...existingTcModel.model.publisherConsents.values()],
    legIntPurposes: [...existingTcModel.model.publisherLegitimateInterests.values()]
  }

  const publisherRestrictions =
    existingTcModel.model.publisherRestrictions.getPurposes()
  return createTcModelForRepromptedUser({
    gvlRaw: newGvlRaw,
    existingTcModel,
    keepDate: true,
    publisherRestrictions,
    publisherConsents
  })
}

/**
 * @param {GVL} gvl
 * @param {string} tcString
 * @returns {[TCModel, Array<import('@mailonline/tcf-core/lib/mjs/model/gvl/Vendor')>]}
 */
export function createModelFromTcString(gvl, tcString) {
  log('creating TCModel from tcString')

  const tcModel = TCString.decode(tcString)

  // controller was originally released with consentScreen=0, this hack will update all those users
  if (tcModel.consentScreen === 0) {
    tcModel.consentScreen = 1
  }
  tcModel.gvl = gvl
  const specialPurposeOnlyDeclared = Object.values(tcModel.gvl.vendors).filter(
    (v) =>
      v.purposes.length === 0 &&
      v.legIntPurposes.length === 0 &&
      v.specialPurposes.length > 0
  )
  log('specialPurposeOnly', specialPurposeOnlyDeclared)
  return [tcModel, specialPurposeOnlyDeclared]
}

/**
 * @param {TCModelFactoryOpts} opts
 * @returns {Promise<TcModel>}
 */
export const create = async ({
  gvl,
  newGvl,
  type,
  tcString,
  existingTcModel,
  publisherRestrictions,
  publisherConsents
}) => {
  switch (type) {
    case 'newUser':
      if (gvl) {
        log('newUser')
        return createTcModelFromGvl({
          gvlRaw: gvl,
          publisherConsents,
          publisherRestrictions
        })
      }
      break

    case 'existingUser':
      if (gvl && tcString) {
        log('existingUser')
        return createTcModelFromTcString(gvl, tcString)
      }
      break

    case 'repromptedUser':
      if (newGvl && existingTcModel) {
        log('repromptedUser')
        return createTcModelForRepromptedUser({
          gvlRaw: newGvl,
          existingTcModel,
          keepDate: false,
          publisherRestrictions,
          publisherConsents
        })
      }
      break

    case 'exitingUserSimplifiedGvl':
      if (gvl && newGvl && tcString) {
        log('exitingUserSimplifiedGvl')
        return createTcModelForExitingUserSimplifiedGvl({
          existingGvlRaw: gvl,
          newGvlRaw: newGvl,
          tcString
        })
      }
      break
  }
  return createTcModelFromGvl({
    gvlRaw: gvl,
    publisherRestrictions,
    publisherConsents
  })
}

/**
 * @typedef {import('../utils/fetch-vendor-lists').GVLRaw} GVLRaw
 */
/**
 * @typedef {Object} TCModelFactoryOpts
 * @property {UserType} type
 * @property {GVLRaw} [gvl]
 * @property {GVLRaw} [newGvl]
 * @property {string} [tcString]
 * @property {TcModel} [existingTcModel]
 * @property {Array<number>} publisherRestrictions
 * @property {PublisherConsents} publisherConsents
 */

/**
 * @typedef {'newUser'|'existingUser'|'repromptedUser'|'exitingUserSimplifiedGvl'} UserType
 */

/**
 * @typedef {Object} PreferenceOpts
 * @property {Array<number>} vendorConsents
 * @property {Array<number>} vendorLegitimateInterests
 * @property {Array<number>} purposeConsents
 * @property {Array<number>} purposeLegitimateInterests
 * @property {Array<number>} specialFeatureOptins
 * @property {string|number} consentScreen
 **/

/**
 * @typedef {import('../config/config').PublisherConsents} PublisherConsents
 */
